import { useState } from 'react';
import type { SubmitCredentialingRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { maskZipInput } from '@/shared/lib/formatters';
import { commitOrPushErrors } from '@/shared/lib/forms';
import { FormField } from '@/shared/ui/primitives';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { FieldGroup } from '@/shared/ui/shadcn/field';
import { Textarea } from '@/shared/ui/shadcn/textarea';

import { maskDobMdyInput } from '../../model/dosespot';
import {
  useCredentialingForm,
  ATTESTATION_SECTIONS,
  findUnansweredSections,
  type AttestationSectionMeta,
} from '../../model/useCredentialingForm';
import type { CredentialingPayload } from '../../model/useClinicianOnboardingOrchestrator';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';

interface CredentialingStepProps {
  onSubmit: (payload: CredentialingPayload) => void;
  isSubmitting: boolean;
  submitError: Error | null;
}

export function CredentialingStep({ onSubmit, isSubmitting, submitError }: CredentialingStepProps) {
  const { form, validateDraft } = useCredentialingForm();
  const [explanation, setExplanation] = useState('');
  const [attestationError, setAttestationError] = useState<string | null>(null);

  function handleSubmit() {
    const appData = form.state.values.application_data as Record<
      string,
      { answers: (boolean | null)[]; explanation: string | null }
    >;

    const unanswered = findUnansweredSections(appData);
    if (unanswered.length > 0) {
      setAttestationError(`Please answer all questions in: ${unanswered.join(', ')}`);
      return;
    }
    setAttestationError(null);

    for (const section of ATTESTATION_SECTIONS) {
      const sectionKey = `attestation_${section.key}`;
      const hasYes = appData[sectionKey]?.answers?.some(Boolean) ?? false;
      form.setFieldValue(
        `${section.fieldName}.explanation` as 'date_of_birth',
        (hasYes ? explanation : null) as unknown as string
      );
    }

    const data = commitOrPushErrors(form, validateDraft());
    if (!data) {
      return;
    }
    onSubmit({ data: data satisfies SubmitCredentialingRequestT });
  }

  return (
    <div className="flex flex-col gap-6">
      {submitError ? (
        <Alert variant="destructive">
          <AlertDescription>{submitError.message}</AlertDescription>
        </Alert>
      ) : null}

      <form
        id={DS_STEP_FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          if (!isSubmitting) {
            handleSubmit();
          }
        }}
        className="flex flex-col gap-6"
      >
        <section>
          <h3 className="mb-3 text-sm font-semibold">Mailing Address</h3>
          <FieldGroup>
            <FormField
              form={form}
              type="text"
              name="mailing_address_street"
              label="Street"
              placeholder="123 Main St"
              inputProps={{ autoComplete: 'street-address' }}
            />
            <FormField
              form={form}
              type="text"
              name="mailing_address_city"
              label="City"
              placeholder="San Francisco"
              inputProps={{ autoComplete: 'address-level2' }}
            />
            <FormField
              form={form}
              type="text"
              name="mailing_address_state"
              label="State"
              placeholder="CA"
              inputProps={{ autoComplete: 'address-level1', maxLength: 2 }}
            />
            <FormField
              form={form}
              type="text"
              name="mailing_address_zip"
              label="ZIP code"
              placeholder="94102"
              parse={maskZipInput}
              inputProps={{ autoComplete: 'postal-code', maxLength: 10, inputMode: 'numeric' }}
            />
          </FieldGroup>
        </section>

        <section>
          <h3 className="mb-3 text-sm font-semibold">Personal Information</h3>
          <FieldGroup>
            <FormField
              form={form}
              type="text"
              name="date_of_birth"
              label="Date of birth"
              placeholder="MM/DD/YYYY"
              parse={maskDobMdyInput}
              inputProps={{ autoComplete: 'bday', inputMode: 'numeric', maxLength: 10 }}
            />
          </FieldGroup>
        </section>

        <section>
          <h3 className="mb-3 text-sm font-semibold">Attestations</h3>
          <p className="text-muted-foreground mb-4 text-xs">
            Please answer every question. If you answered &quot;Yes&quot; to any question, provide a
            brief explanation below.
          </p>

          {attestationError ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{attestationError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col gap-6">
            {ATTESTATION_SECTIONS.map((section) => (
              <AttestationSectionPanel key={section.key} form={form} section={section} />
            ))}
          </div>

          <div className="mt-6">
            <label htmlFor="attestation-explanation" className="mb-1.5 block text-xs font-medium">
              If you answered &quot;Yes&quot; to any of the above, please explain
            </label>
            <Textarea
              id="attestation-explanation"
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Provide details here…"
              rows={3}
              className="text-xs"
            />
          </div>
        </section>
      </form>
    </div>
  );
}

interface AttestationSectionPanelProps {
  form: ReturnType<typeof useCredentialingForm>['form'];
  section: AttestationSectionMeta;
}

function AttestationSectionPanel({ form, section }: AttestationSectionPanelProps) {
  return (
    <div className="rounded-lg border p-4">
      <h4 className="mb-3 text-xs font-semibold tracking-wide">{section.title}</h4>
      <div className="flex flex-col gap-3">
        {section.questions.map((question, qi) => (
          <form.Field
            key={qi}
            name={`${section.fieldName}.answers[${qi}]` as 'date_of_birth'}
            children={(field) => {
              const value = field.state.value as unknown as boolean | null;
              return (
                <AttestationQuestion
                  question={question}
                  value={value}
                  onChange={(v) => (field.handleChange as unknown as (val: boolean) => void)(v)}
                  id={`${section.key}-q-${qi}`}
                />
              );
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface AttestationQuestionProps {
  question: string;
  value: boolean | null;
  onChange: (value: boolean) => void;
  id: string;
}

function AttestationQuestion({ question, value, onChange, id }: AttestationQuestionProps) {
  const selected = 'rounded-md px-2.5 py-1 text-[11px] font-medium ring-1 ring-primary';
  const unselected =
    'rounded-md px-2.5 py-1 text-[11px] font-medium text-muted-foreground ring-1 ring-muted hover:ring-muted-foreground/50';

  return (
    <div className="flex items-start gap-3">
      <p className="min-w-0 flex-1 text-xs leading-relaxed">{question}</p>
      <div className="flex shrink-0 gap-1">
        <button
          type="button"
          onClick={() => onChange(true)}
          aria-pressed={value === true}
          className={value === true ? selected : unselected}
          id={`${id}-yes`}
        >
          Yes
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          aria-pressed={value === false}
          className={value === false ? selected : unselected}
          id={`${id}-no`}
        >
          No
        </button>
      </div>
    </div>
  );
}
